import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CreateIcon from '@material-ui/icons/Create';
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { CardHeader } from "@material-ui/core";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import { FileCopy } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import { resolveTicketChannel } from "../../utils/serviceWindow";

function channelLabelKey(channel) {
  return `contactDrawer.channels.${String(channel).replace(/-/g, "_")}`;
}

function formatTicketChannelLabel(channel) {
  return i18n.t(channelLabelKey(channel), channel);
}

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		background: theme.palette.total,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		background: theme.palette.total,
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 100,
		height: 100,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading, onContactUpdate }) => {
	const classes = useStyles();
	const [currentContact, setCurrentContact] = useState(contact);
	const [modalOpen, setModalOpen] = useState(false);
	const [openForm, setOpenForm] = useState(false);

	// Función para detectar si un contacto es de webchat
	const isWebChatContact = (ticket) => resolveTicketChannel(ticket) === "webchat";

	// Detectar si es contacto de webchat
	const isWebChat = isWebChatContact(ticket);
	const ticketChannel = resolveTicketChannel(ticket);
	const channelLabel = formatTicketChannelLabel(ticketChannel);

	useEffect(() => {
		if (contact) {
			setCurrentContact(contact);
			const savedContacts = JSON.parse(localStorage.getItem('contacts') || '{}');
			if (savedContacts[contact.id]) {
				setCurrentContact(prevContact => ({
					...prevContact,
					...savedContacts[contact.id]
				}));
			}
		}
		setOpenForm(false);
	}, [contact, open]);

	const handleContactUpdate = (updatedContact) => {
		const newContact = {
			...currentContact,
			...updatedContact
		};
		setCurrentContact(newContact);

		// Actualizar el localStorage
		const updatedContacts = JSON.parse(localStorage.getItem('contacts') || '{}');
		updatedContacts[newContact.id] = newContact;
		localStorage.setItem('contacts', JSON.stringify(updatedContacts));

		if (onContactUpdate && ticket) {
			onContactUpdate(ticket.uuid, newContact);
		}
	};

	const handleCopyNumber = () => {
		// Solo copiar si no es webchat
		if (!isWebChat) {
			navigator.clipboard.writeText(currentContact.number);
		}
	};

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="persistent"
				anchor="right"
				open={open}
				PaperProps={{ style: { position: "absolute" } }}
				BackdropProps={{ style: { position: "absolute" } }}
				ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					<Typography style={{ justifySelf: "center" }}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<Paper square variant="outlined" className={classes.contactHeader}>
							<CardHeader
								onClick={() => {}}
								style={{ cursor: "pointer", width: '100%' }}
								titleTypographyProps={{ noWrap: true }}
								subheaderTypographyProps={{ noWrap: true }}
								avatar={<Avatar src={currentContact.profilePicUrl} alt="contact_image" style={{ width: 60, height: 60 }} />}
								title={
									<>
										<Typography onClick={() => setOpenForm(true)}>
											{currentContact.name}
											<CreateIcon style={{fontSize: 16, marginLeft: 5}} />
										</Typography>
									</>
								}
								subheader={
									<>
										{!isWebChat && currentContact.number && (
											<Typography style={{ fontSize: 12, display: "flex", alignItems: "center" }}>
												<Link href={`tel:${currentContact.number}`}>{currentContact.number}</Link>
												<IconButton
													size="small"
													onClick={handleCopyNumber}
													style={{ marginLeft: 5 }}
												>
													<FileCopy style={{ fontSize: 16 }} />
												</IconButton>
											</Typography>
										)}
										<Typography style={{ fontSize: 12, color: "#666" }}>
											{i18n.t("contactDrawer.channel")}: {channelLabel}
										</Typography>
										{currentContact.email && (
											<Typography style={{ fontSize: 12 }}>
												<Link href={`mailto:${currentContact.email}`}>{currentContact.email}</Link>
											</Typography>
										)}
									</>
								}
							/>
							<Button
								variant="outlined"
								color="primary"
								onClick={() => setOpenForm(true)}
								style={{fontSize: 12}}
							>
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>
							{(currentContact.id && openForm) && (
								<ContactForm 
									initialContact={currentContact} 
									onCancel={() => setOpenForm(false)}
									onSave={handleContactUpdate}
								/>
							)}
						</Paper>
						<Paper square variant="outlined" className={classes.contactDetails}>
							<Typography variant="subtitle1" style={{marginBottom: 10}}>
								<MarkdownWrapper>{currentContact.info}</MarkdownWrapper>
							</Typography>
							<ContactNotes ticket={ticket} />
						</Paper>
					</div>
				)}
			</Drawer>
		</>
	);
};

export default ContactDrawer;
